import { useQuery } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { instance } from "api/apiBase";
import moment from "moment";
import { useMemo } from "react";
import { Table } from "shared/app/Table";
import { formatToCurrency } from "shared/utils";
import { CustomerTransactionData } from "views/rent-reporting-id/types";
import { ErrorSection } from "./components/ErrorSection";
import { getErrorMessage } from "utils/error";

interface PmsLedgerTableProps {
  renterId: string;
}

export const PmsLedgerTable = ({ renterId }: PmsLedgerTableProps) => {
  const {
    data: rawData,
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: ["SUBSCRIBER_TRANSACTIONS", renterId],
    queryFn: () =>
      instance
        .get<
          CustomerTransactionData[]
        >(`/portal/v1/customers/${renterId}/transactions`)
        .then((data) => data)
  });

  const tableData = useMemo(() => {
    return rawData?.data || [];
  }, [rawData?.data]);

  const columns = useMemo(() => {
    return [
      {
        id: "date fixed_width",
        size: 50,
        header: "Date",
        accessorKey: "date",
        cell: (info) => {
          return (
            <span className="font-bold">
              {moment.utc(info.getValue()).format("MMM DD, YYYY")}
            </span>
          );
        }
      },
      {
        id: "description fixed_width",
        size: 160,
        header: "Description",
        accessorKey: "description",
        cell: (info) => {
          return <span className="text-content-1000">{info.getValue()}</span>;
        }
      },
      {
        id: "type fixed_width",
        size: 50,
        header: "Type",
        accessorKey: "type",
        cell: (info) => {
          return <span className="font-bold">{info.getValue()}</span>;
        }
      },
      {
        id: "amount fixed_width",
        size: 50,
        header: "Amount",
        accessorKey: "amount",
        cell: (info) => {
          const total = info.getValue() * 100;

          return (
            <span className="font-bold">
              {formatToCurrency({
                cents: total,
                currency: "USD"
              })}
            </span>
          );
        }
      }
    ];
  }, []);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  if (isError) return <ErrorSection errorMessage={getErrorMessage(error)} />;

  return (
    <Table
      table={table}
      emptyStateTitle="No transactions found"
      isLoading={isFetching}
      mobileRow={(row) => {
        return (
          <div className="space-y-2">
            <p className="text-sm font-medium text-content-1000">
              {row.original.description}
            </p>
            <p className="text-xs text-content-400 my-1">
              <span> Type: {row.original.type || "-"} </span>
            </p>
            <p className="text-sm text-content-700">
              <span>
                {row.original.amount
                  ? formatToCurrency({
                      cents: row.original.amount
                        ? row.original.amount * 100
                        : 0,
                      currency: "USD"
                    })
                  : "-"}
              </span>
            </p>
            <p className="text-sm text-content-700">
              <span>
                {row.original.date
                  ? moment.utc(row.original.date).format("MMM DD, YYYY")
                  : ""}
              </span>
            </p>
          </div>
        );
      }}
    />
  );
};
